import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Building2 } from "lucide-react";
import LogoutDialog from "./LogoutDialog";
import Tooltip from "../components/ui/Tooltip";
import Avatar from "../components/ui/Avatar";
import { ModuleBadge, ModuleBadgeMini, BadgeLayer } from "./SidebarBadge";
import { getModuleIcon as getIconComponent } from "../utils/module-icon";

export default function Sidebar({ isOpen, toggleSidebar, isMobile, mobileOpen, setMobileOpen }) {
  const { user, assignedModules, loading } = useContext(AuthContext);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});

  const location = useLocation();
  // Collapsed desktop rail: full-size badges may overflow past the sidebar edge.
  const showRailBadges = !isOpen && !isMobile;

  const isSubmenuActive = (submenu) => {
    return submenu?.some(item =>
      location.pathname.startsWith(item.path)
    );
  };


  const toggleSubmenu = (itemId) => {
    setOpenSubmenus(prev => ({
      [itemId]: !prev[itemId] // only keep current
    }));
  };

  // Parent module click. On the collapsed desktop rail the children have
  // nowhere to show, so expand the sidebar and open this module's submenu.
  const handleParentClick = (itemId) => {
    if (!isOpen && !isMobile) {
      toggleSidebar();
      setOpenSubmenus({ [itemId]: true });
      return;
    }
    if (isOpen) toggleSubmenu(itemId);
  };
  // Auto-open submenu if any child is active
  useEffect(() => {
    const activeParent = assignedModules.find(item =>
      item.child_modules && isSubmenuActive(item.child_modules)
    );

    if (activeParent) {
      setOpenSubmenus({ [activeParent.id]: true });
    }
  }, [location.pathname, assignedModules]);
  const sidebarVariants = {
    open: {
      x: 0,
      width: "280px",
      transition: {
        x: { type: "spring", stiffness: 200, damping: 25 },
        width: { duration: 0.25, ease: "easeInOut" },
      },
    },
    closed: {
      x: isMobile ? -280 : 0,
      width: "80px",
      transition: {
        x: { type: "spring", stiffness: 240, damping: 28 },
        width: { duration: 0.25, ease: "easeInOut" },
      },
    },
  };

  const overlayVariants = {
    open: { opacity: 1, pointerEvents: "auto" },
    closed: { opacity: 0, pointerEvents: "none" },
  };

  const submenuVariants = {
    open: {
      height: "auto",
      opacity: 1,
      transition: {
        height: { duration: 0.3, ease: "easeInOut" },
        opacity: { duration: 0.2, delay: 0.1 },
      },
    },
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        height: { duration: 0.3, ease: "easeInOut" },
        opacity: { duration: 0.2 },
      },
    },
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  return (
    <>
      {/* Overlay for mobile */}
      <AnimatePresence>
        {mobileOpen && isMobile && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999]"
            initial="closed"
            animate="open"
            exit="closed"
            variants={overlayVariants}
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        className={`fixed left-0 top-0 bottom-0 z-[1000] flex flex-col ${showRailBadges ? "overflow-visible" : "overflow-hidden"} bg-gradient-to-b from-primary to-primary_hover text-white border-r border-white/10 shadow-[4px_0_32px_rgba(0,0,0,0.45)] ${isMobile && !mobileOpen ? "-translate-x-full" : ""
          }`}
        initial={isMobile ? "closed" : "open"}
        animate={
          isMobile
            ? mobileOpen
              ? "open"
              : "closed"
            : isOpen
              ? "open"
              : "closed"
        }
        variants={sidebarVariants}
      >
        {/* Logo Section */}
        <div className={`min-h-[80px] flex items-center border-b border-white/10 transition-all duration-300 ${isOpen ? "px-5 py-4" : "justify-center py-4"}`}>
          <div className={`flex items-center min-w-0 overflow-hidden ${isOpen ? "gap-3 w-full" : "justify-center"}`}>
            <motion.div
              className="flex-shrink-0 rounded-xl bg-gradient-to-r from-secondary to-secondary_hover flex items-center justify-center text-white shadow-[0_4px_16px_rgba(134,43,43,0.45)]"
              initial={false}
              animate={{
                width: isOpen ? 44 : 46,
                height: isOpen ? 44 : 46,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              {user?.company?.company_url ? (
                <img
                  src={user.company.company_url}
                  alt={user?.company?.company_name || "Company Logo"}
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                <Building2 size={isOpen ? 26 : 28} />
              )}
            </motion.div>

            {isOpen && (
              <motion.span
                className="text-base font-bold tracking-wide text-white truncate min-w-0 flex-1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                title={user?.company?.company_name || "Your Company"}
              >
                {user?.company?.company_name || "Your Company"}
              </motion.span>
            )}
          </div>
        </div>

        {/* Menu Items */}
        <div className="relative flex-1 min-h-0 flex flex-col">
        <BadgeLayer enabled={showRailBadges}>
        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
          <motion.ul
            className="list-none p-2 m-0 w-full flex flex-col items-center"
            animate={isOpen ? "open" : "closed"}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-40 px-4">
                <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-white animate-spin mb-4"></div>
                <p className="text-sm text-white/60 tracking-wide">
                  Preparing your workspace just a moment...
                </p>
              </div>) : (

              // menuItems.map((item) => {
              assignedModules.map((item) => {
                const Icon = getIconComponent(item.icon);
                const hasSubmenu = item.child_modules && item.child_modules.length > 0;
                const isSubmenuActiveFlag = hasSubmenu && isSubmenuActive(item.child_modules);
                const isOpenSubmenu = openSubmenus[item.id] || isSubmenuActiveFlag;

                return (
                  <motion.li
                    key={item.id}
                    className="w-full my-1.5"
                  >
                    {hasSubmenu ? (
                      // Menu item with submenu
                      <>
                        <Tooltip content={item.name} placement="right" variant="brand" fullWidth>
                        <div
                          className={`relative flex items-center gap-2 px-2 py-1 rounded-lg text-white/70 no-underline transition-all duration-300 overflow-hidden cursor-pointer ${!isOpen ? "w-[60px] p-2 justify-center" : "w-full"
                            } ${isSubmenuActiveFlag
                              ? "bg-white/[0.13] text-white"
                              : "hover:bg-white/[0.08] hover:text-white"
                            }`}
                          onClick={() => handleParentClick(item.id)}
                        >
                          <div
                            className={`relative w-[34px] h-[34px] flex items-center justify-center rounded-lg transition-all duration-300 flex-shrink-0 p-1.5 ${isSubmenuActiveFlag ? "bg-secondary text-white" : "bg-white/10 text-white"
                              }`}
                          >
                            <Icon size={isOpen ? 22 : 24} />
                            {!isOpen && <ModuleBadgeMini badge={item.badge} />}
                          </div>
                          {isOpen && (
                            <>
                              <span className="text-sm font-medium flex-1 min-w-0 truncate">
                                {item.name}
                              </span>
                              <ModuleBadge badge={item.badge} />
                              <motion.div
                                animate={{ rotate: isOpenSubmenu ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Icons.ChevronDown size={16} />
                              </motion.div>
                            </>
                          )}
                          {isSubmenuActiveFlag && !isOpen && (
                            <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1 h-5 bg-secondary rounded-r-full shadow-[0_0_8px_rgba(134,43,43,0.7)]"></div>
                          )}
                          {isSubmenuActiveFlag && isOpen && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4/5 bg-secondary rounded-r-full shadow-[0_0_8px_rgba(134,43,43,0.6)]"></div>
                          )}
                        </div>
                        </Tooltip>

                        {/* Submenu items */}
                        {isOpen && (
                          <AnimatePresence initial={false}>
                            {isOpenSubmenu && (
                              <motion.ul
                                className="list-none mt-1 ml-8 pl-2 border-l border-white/15"
                                variants={submenuVariants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                              >
                                {item.child_modules.map((subItem) => {
                                  // const isActive = location.pathname === subItem.path;
                                  const isActive = location.pathname.startsWith(subItem.path);
                                  return (
                                    <motion.li
                                      key={subItem.id}
                                      className="w-full my-1"
                                    >
                                      <Tooltip content={subItem.name} placement="right" variant="brand" fullWidth>
                                      <NavLink
                                        to={subItem.path}
                                        className={`relative flex items-center gap-2 px-2 py-1.5 rounded-lg text-white/70 no-underline transition-all duration-300 ${isActive
                                          ? "bg-white/15 text-white"
                                          : "hover:bg-white/10 hover:text-white"
                                          }`}
                                        onClick={() => {
                                          setOpenSubmenus({});
                                          if (isMobile) setMobileOpen(false);
                                        }}

                                      >
                                        <div className="w-1.5 h-1.5 rounded-full bg-current opacity-50 flex-shrink-0"></div>
                                        <span className="text-sm font-medium flex-1 truncate min-w-0">
                                          {subItem.name}
                                        </span>
                                        <ModuleBadge badge={subItem.badge} />
                                        {isActive && (
                                          <motion.div
                                            className="flex-shrink-0 w-1.5 h-1.5 bg-secondary rounded-full shadow-[0_0_10px_rgba(134,43,43,0.8)]"
                                            layoutId="activeSubIndicator"
                                            initial={false}
                                          />
                                        )}
                                      </NavLink>
                                      </Tooltip>
                                    </motion.li>
                                  );
                                })}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        )}
                      </>
                    ) : (
                      // Regular menu item without submenu
                      <Tooltip content={item.name} placement="right" variant="brand" fullWidth>
                      <NavLink
                        to={item.path}
                        className={`relative flex items-center gap-2 px-2 py-1 rounded-lg text-white/70 no-underline transition-all duration-300 overflow-hidden ${!isOpen ? "w-[60px] p-2 justify-center" : "w-full"
                          } ${location.pathname === item.path
                            ? "bg-white/[0.13] text-white"
                            : "hover:bg-white/[0.08] hover:text-white"
                          }`}
                        onClick={() => {
                          setOpenSubmenus({});
                          if (isMobile) setMobileOpen(false);
                        }}
                      >
                        <div
                          className={`relative w-[34px] h-[34px] flex items-center justify-center rounded-lg transition-all duration-300 flex-shrink-0 p-1.5 ${location.pathname === item.path ? "bg-secondary text-white" : "bg-white/10 text-white"
                            }`}
                        >
                          <Icon size={isOpen ? 22 : 24} />
                          {!isOpen && <ModuleBadgeMini badge={item.badge} />}
                        </div>
                        {isOpen && (
                          <>
                            <span className="text-sm font-medium flex-1 min-w-0 truncate">
                              {item.name}
                            </span>
                            <ModuleBadge badge={item.badge} />
                          </>
                        )}
                        {location.pathname === item.path && isOpen && (
                          <>
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4/5 bg-secondary rounded-r-full shadow-[0_0_8px_rgba(134,43,43,0.6)]"></div>
                            {!item.badge && (
                              <motion.div
                                className="absolute right-4 w-2 h-2 bg-secondary rounded-full shadow-[0_0_10px_rgba(134,43,43,0.8)]"
                                layoutId="activeIndicator"
                                initial={false}
                              />
                            )}
                          </>
                        )}
                        {location.pathname === item.path && !isOpen && (
                          <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1 h-5 bg-secondary rounded-r-full shadow-[0_0_8px_rgba(134,43,43,0.7)]"></div>
                        )}
                      </NavLink>
                      </Tooltip>
                    )}
                  </motion.li>
                );
              })
            )}
          </motion.ul>
        </nav>
        </BadgeLayer>
        </div>

        {/* User Profile & Logout */}
        {isOpen && (
          <motion.div
            className="p-5 border-t border-white/10 bg-black/15"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-5">
              {/* Same Avatar as the collapsed rail, so photo / initials colour match when toggling */}
              <div className="flex flex-shrink-0 rounded-xl overflow-hidden ring-2 ring-white/20 ring-offset-1 ring-offset-transparent">
                <Avatar user={user} size={48} rounded={false} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="m-0 text-sm font-semibold text-white truncate">
                  {user?.first_name} {user?.last_name}
                </h4>
                <p className="m-0 mt-0.5 text-xs text-white/50 truncate">
                  {user?.email}
                </p>
              </div>
            </div>

            <motion.button
              className="w-full flex items-center justify-center gap-3 p-3 bg-white/10 border border-white/15 rounded-xl text-white text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-secondary hover:border-secondary group"
              onClick={handleLogoutClick}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.97 }}
            >
              <motion.span
                className="flex items-center"
                whileHover={{ x: -2 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Icons.LogOut size={18} />
              </motion.span>
              <span>Sign Out</span>
            </motion.button>
          </motion.div>
        )}

        {/* Mini user avatar when collapsed (photo, or initials when there is none).
            Clicking it opens the sidebar. onMouseDown keeps the click from
            focusing the button, so the focus ring only shows for keyboard users. */}
        {!isOpen && !isMobile && user && (
          <div className="py-4 flex justify-center border-t border-white/10">
            <button
              type="button"
              onClick={toggleSidebar}
              onMouseDown={(e) => e.preventDefault()}
              title={`${user.first_name || ""} ${user.last_name || ""}`.trim()}
              aria-label="Open sidebar"
              className="flex rounded-xl overflow-hidden ring-2 ring-white/20 cursor-pointer focus-visible:outline-none focus-visible:ring-white/60"
            >
              <Avatar user={user} size={40} rounded={false} />
            </button>
          </div>
        )}
      </motion.aside>

      {/* Logout Confirmation Dialog */}
      <LogoutDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        collapsed={!isOpen && !isMobile}
      />
    </>
  );
}